import cn from 'clsx'
import {parseISO, isSameDay, startOfDay} from 'date-fns'
import {Space} from 'antd'

import styles from './CashflowList.module.scss'
import {DrawdownTag} from 'modules/project'
import {
  Block,
  Circle,
  Text,
  Box,
  Button,
  CentsMoney,
  IconButton,
  RotateArrowThirdIcon,
  RotateArrowFourthIcon,
  DotsIcon,
} from 'common/components'
import {useOpen} from 'common/hooks'
import {
  formatDate,
  formatMoney,
  isItemDrawdown,
  maybePluralize,
} from 'common/helpers'

interface CashflowListProps {
  isMonthDetails?: boolean
  cashflowItems: any[]
  drawdownsCount: number
  repaymentsCount: number
  drawdownsAmount: number
  repaymentsAmount: number
}

interface ContentProps extends CashflowListProps {
  onClose?: () => void
}

interface CashflowDayProps {
  isCashflowForecast: boolean
  cashflowDay: any
}

interface ItemProps {
  isCashflowForecast: boolean
  item: any
  index: number
}

interface BarProps {
  isCashflowForecast: boolean
  drawdownsCount: number
  repaymentsCount: number
  drawdownsAmount: number
  repaymentsAmount: number
}

interface BarColumnProps {
  isCashflowForecast: boolean
  label: string
  value: number
}

export default function CashflowList({
  isMonthDetails,
  ...props
}: CashflowListProps) {
  if (isMonthDetails) return <Content {...props} />

  return <ToggleContent {...props} />
}

const ToggleContent = ({
  cashflowItems,
  drawdownsCount,
  repaymentsCount,
  drawdownsAmount,
  repaymentsAmount,
}: ContentProps) => {
  const [isOpen, handleOpen, handleClose] = useOpen()

  if (isOpen)
    return (
      <Content
        cashflowItems={cashflowItems}
        drawdownsCount={drawdownsCount}
        repaymentsCount={repaymentsCount}
        drawdownsAmount={drawdownsAmount}
        repaymentsAmount={repaymentsAmount}
        onClose={handleClose}
      />
    )

  return (
    <IconButton block className={styles.button} onClick={handleOpen}>
      <DotsIcon />
    </IconButton>
  )
}

const Content = ({
  cashflowItems,
  drawdownsCount,
  repaymentsCount,
  drawdownsAmount,
  repaymentsAmount,
  onClose,
}: ContentProps) => {
  const isCashflowForecast = !onClose
  const cashflowDays = getCashflowDays(cashflowItems)

  return (
    <div className={cn(styles.root, onClose && styles.root_toggle)}>
      <ul className={styles.list}>
        {cashflowDays.map((cashflowDay) => (
          <CashflowDay
            key={cashflowDay.date}
            isCashflowForecast={isCashflowForecast}
            cashflowDay={cashflowDay}
          />
        ))}
      </ul>
      <Bar
        isCashflowForecast={isCashflowForecast}
        drawdownsCount={drawdownsCount}
        repaymentsCount={repaymentsCount}
        drawdownsAmount={drawdownsAmount}
        repaymentsAmount={repaymentsAmount}
      />
      {onClose && (
        <Box align="center">
          <Button variant="secondary" onClick={onClose}>
            Hide
          </Button>
        </Box>
      )}
    </div>
  )
}

const CashflowDay = ({isCashflowForecast, cashflowDay}: CashflowDayProps) => {
  const Component = cashflowDay.estimated ? 'li' : Block

  return (
    <Component
      as={cashflowDay.estimated ? undefined : 'li'}
      className={cn(styles.day, cashflowDay.estimated && styles.day_estimated)}
      type={cashflowDay.estimated ? undefined : 'grey'}
    >
      {cashflowDay.items.map((item: any, index: number) => (
        <Item
          key={index}
          isCashflowForecast={isCashflowForecast}
          item={item}
          index={index}
        />
      ))}
    </Component>
  )
}

const Item = ({isCashflowForecast, item, index}: ItemProps) => {
  const transacted = item.source === 'transaction'
  const isDrawdown = isItemDrawdown(item)
  const amountColor = transacted ? undefined : 'turquoizeDeep'

  return (
    <div className={styles.grid}>
      {index === 0 ? (
        <Text weight="demiBold">{formatDate(item.date, 'fifth')}</Text>
      ) : (
        <div />
      )}
      <Space size={20}>
        <Circle
          border={!transacted}
          color={transacted ? (isDrawdown ? 'darkGrey' : 'green') : 'turquoize'}
        />
        <span>{item.projectTitle}</span>
      </Space>
      <Space size={10}>
        {transacted &&
          (isDrawdown ? <RotateArrowFourthIcon /> : <RotateArrowThirdIcon />)}
        <Text as="span" size="xs" color={transacted ? undefined : 'grey'}>
          {transacted ? '' : 'Estimated '}
          {isDrawdown ? 'drawdown' : 'repayment'}
        </Text>
      </Space>
      {!isCashflowForecast && (
        <div>
          {item.drawdown && <DrawdownTag status={item.drawdown.status} />}
        </div>
      )}
      {isDrawdown ? (
        <Text align="right" color={amountColor}>
          {formatMoney(item.amount, {
            prefix: false,
            cents: true,
            parentheses: isCashflowForecast,
          })}
        </Text>
      ) : (
        <div />
      )}
      {isDrawdown ? (
        <div />
      ) : (
        <Text align="right" color={amountColor}>
          {formatMoney(Math.abs(item.amount), {
            prefix: false,
            cents: true,
            parentheses: !isCashflowForecast,
          })}
        </Text>
      )}
      <CentsMoney
        parentheses={
          isCashflowForecast ? item.cashflowAmount > 0 : item.cashflowAmount < 0
        }
        prefix={false}
        size="lg"
        weight="medium"
        value={Math.abs(item.cashflowAmount)}
        align="right"
      />
    </div>
  )
}

const Bar = ({
  isCashflowForecast,
  drawdownsCount,
  repaymentsCount,
  drawdownsAmount,
  repaymentsAmount,
}: BarProps) => {
  return (
    <Block className={cn(styles.grid, styles.grid_bar)} type="black">
      <Box weight="demiBold" size="2xl">
        <Space size={12}>
          <span>{maybePluralize(drawdownsCount, 'drawdown')}</span>
          <span>{maybePluralize(repaymentsCount, 'repayment')}</span>
        </Space>
      </Box>
      <BarColumn
        isCashflowForecast={isCashflowForecast}
        label="withdrawn"
        value={drawdownsAmount}
      />
      <BarColumn
        isCashflowForecast={isCashflowForecast}
        label="repaid"
        value={repaymentsAmount}
      />
      <BarColumn
        isCashflowForecast={isCashflowForecast}
        label="cashflow"
        value={drawdownsAmount - repaymentsAmount}
      />
    </Block>
  )
}

const BarColumn = ({isCashflowForecast, label, value}: BarColumnProps) => {
  const parentheses = isCashflowForecast
    ? label === 'withdrawn' || (label === 'cashflow' && value > 0)
    : label === 'repaid' || value < 0

  return (
    <Box align="right">
      <Text size="xs" color="white50" mb={4}>
        {label}
      </Text>
      <CentsMoney
        parentheses={parentheses}
        size="2xl"
        weight="demiBold"
        value={Math.abs(value)}
      />
    </Box>
  )
}

const getCashflowDays = (items: any[]): any[] => {
  const today = new Date()
  const sortedItems = items.slice()
  sortedItems.sort((a, b) => +parseISO(a.date) - +parseISO(b.date))
  let cashflowAmount = 0
  return sortedItems.reduce((arr: any[], item: any) => {
    const extendedItem = {
      ...item,
      cashflowAmount: (cashflowAmount += isItemDrawdown(item)
        ? item.amount
        : -item.amount),
    }
    const date = parseISO(item.date)
    const cashflowDay = arr.find((arrItem) => isSameDay(arrItem.date, date))
    if (cashflowDay) {
      cashflowDay.items.push(extendedItem)
    } else {
      arr.push({
        date,
        estimated: startOfDay(date) >= startOfDay(today),
        items: [extendedItem],
      })
    }
    return arr
  }, [])
}
